import React, { useEffect, useState, useRef } from "react";
import { useDraftForm } from "../../context/drafts/DraftForm";
import { useRestoredSalesForm, isInitialRestoredPickup } from "../../services/useSalesFormStore";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import BuyerDetails from "../../components/BuyerDetails";
import PrimaryDocument from "../../components/PrimaryDocument";
import TransactionTable from "../../components/TransactionTable";
import DrawingStatusMaster from "../../components/master/DrawingStatusMaster";
import { displayToast } from "../../utils/Toast";
import { useSalesFormStore } from "../../services/useSalesFormStore";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { useErrorHandler } from "../../services/ErrorHandler";
import Edit from "../../assets/svgIcons/FormEdit.svg";
import Enquiry from "../../assets/svgIcons/Enquiry.svg";
import Print from "../../assets/svgIcons/FormPrint.svg";
import Copy from "../../assets/svgIcons/Copy.svg";
import Delete from "../../assets/svgIcons/FormDelete.svg";
import Quotation from "../../assets/svgIcons/Quotation.svg";
import Loader from "../../components/Loader";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  fetchTransactionByCode,
  fetchTransactionByCodeAndId,
  deleteTransactionByCode,
  updateJobCardStatus,
  fetchQuotationVersions,
} from "../../apis/transaction";
import { fetchRouteCard } from "../../apis/processCard";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useMode } from "../../context/ModeContext";
import "../../styles/pages/formBulder/SalesForm.css";
import "../../styles/components/DownloadFormatModal.css";

const getStatusToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isStartedToken = (value) =>
  [
    "started",
    "start",
    "in_progress",
    "in progress",
    "1",
    "true",
    "yes",
    "y",
    "active",
    "on",
  ].includes(getStatusToken(value));

const isFinishedToken = (value) =>
  [
    "finished",
    "finish",
    "completed",
    "complete",
    "2",
    "done",
    "closed",
  ].includes(getStatusToken(value));

const getWorkOrderStatusCacheKey = (tranId) => `wo_status_${tranId}`;

const getWorkOrderStatus = (head = {}) => {
  const statusCandidates = [
    head?.finish,
    head?.status,
    head?.route_card_finish,
    head?.job_card_finish,
    head?.final_status_of_wo,
    head?.final_status,
    head?.wo_status,
    head?.work_order_status,
    head?.job_card_status,
    head?.route_card_status,
    head?.workorder_status,
  ];

  const hasFinishedCandidate = statusCandidates.some((value) =>
    isFinishedToken(value),
  );

  const hasStartedCandidate = [
    ...statusCandidates,
    head?.start,
    head?.route_card_start,
    head?.job_card_start,
  ].some((value) => isStartedToken(value));

  if (hasFinishedCandidate) {
    return "finished";
  }

  if (hasStartedCandidate) {
    return "started";
  }

  const statusLikeValues = Object.entries(head)
    .filter(([key]) => /(status|start|finish)/i.test(String(key || "")))
    .map(([, value]) => value);

  if (statusLikeValues.some((value) => isFinishedToken(value))) {
    return "finished";
  }

  if (statusLikeValues.some((value) => isStartedToken(value))) {
    return "started";
  }

  if (
    isFinishedToken(head?.finish) ||
    isFinishedToken(head?.status) ||
    isFinishedToken(head?.route_card_finish) ||
    isFinishedToken(head?.job_card_finish)
  ) {
    return "finished";
  }

  if (
    isStartedToken(head?.start) ||
    isStartedToken(head?.status) ||
    isStartedToken(head?.route_card_start) ||
    isStartedToken(head?.job_card_start)
  ) {
    return "started";
  }

  return "not_started";
};

const normalizeWorkOrderHeadStatus = (head = {}) => {
  const resolvedStatus = getWorkOrderStatus(head);

  if (resolvedStatus === "finished") {
    return {
      ...head,
      status: "finished",
      finish: head?.finish || "finished",
      route_card_finish: head?.route_card_finish || "finished",
      job_card_finish: head?.job_card_finish || "finished",
    };
  }

  if (resolvedStatus === "started") {
    return {
      ...head,
      status: "started",
      start: head?.start || "started",
      route_card_start: head?.route_card_start || "started",
      job_card_start: head?.job_card_start || "started",
    };
  }

  return head;
};

const getWorkOrderCardLabel = (typeValue = "") => {
  const currentType = String(typeValue || "")
    .trim()
    .toLowerCase();

  if (
    currentType === "job work" ||
    currentType === "jobwork" ||
    currentType === "job card" ||
    currentType === "jobcard"
  ) {
    return "Job Card";
  }

  return "Route Card";
};

const hasCreatedRouteCard = (item = {}) => {
  const createJobCardToken = String(
    item?.create_jobcard ??
      item?.CREATE_JOBCARD ??
      item?.job_card_created ??
      "",
  )
    .trim()
    .toLowerCase();

  const hasJobCardCreatedFlag = [
    "success",
    "created",
    "1",
    "true",
    "yes",
    "y",
  ].includes(createJobCardToken);

  return Boolean(
    item?.route_card ||
    item?.route_card_id ||
    item?.route_card_no ||
    item?.routecard_id ||
    item?.route_card_head_id ||
    item?.route_card_created ||
    item?.jc_tran_id ||
    item?.job_card_id ||
    item?.jobcard_id ||
    hasJobCardCreatedFlag,
  );
};

const getRouteCardLookupId = (item = {}) => {
  const candidates = [
    item?.route_card_id,
    item?.routecard_id,
    item?.route_card_head_id,
    item?.jc_tran_id,
    item?.job_card_id,
    item?.jobcard_id,
  ];

  const found = candidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  const numericId = Number(found);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

const getRouteCardResponseItems = (response) =>
  Array.isArray(response?.item_data)
    ? response.item_data
    : Array.isArray(response?.data?.item_data)
      ? response.data.item_data
      : [];

const isRouteCardFinished = (response) => {
  if (!response) {
    return false;
  }

  const head = response?.head_data || response?.data?.head_data || {};

  const hasHeadFinishStatus =
    isFinishedToken(head?.finish) ||
    isFinishedToken(head?.status) ||
    isFinishedToken(head?.route_card_finish) ||
    isFinishedToken(head?.job_card_finish);

  if (!hasHeadFinishStatus) {
    return false;
  }

  const items = getRouteCardResponseItems(response);
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.every((item) => {
    const timeTaken = Number(item?.time_taken ?? item?.timeTaken ?? 0);
    return Number.isFinite(timeTaken) && timeTaken > 0;
  });
};

const isRouteCardLocallyFinished = (item = {}) =>
  Boolean(item?.route_card_head_finished) ||
  isFinishedToken(item?.route_card_finish) ||
  isFinishedToken(item?.job_card_finish);

const getWorkOrderRouteCardFinishCacheKey = (tranId) =>
  `wo_route_card_finish_${tranId}`;

const mergeItemDetails = (baseItems = [], draftItems = []) => {
  const maxLength = Math.max(baseItems.length, draftItems.length);
  return Array.from({ length: maxLength }, (_, index) => ({
    ...(baseItems[index] || {}),
    ...(draftItems[index] || {}),
  })).filter((item) => Object.keys(item).length > 0);
};

const resolveWorkOrderItemJobCardId = async ({
  item,
  itemIndex,
  headDetails,
}) => {
  try {
    const response = await fetchTransactionByCode({
      transactionCode: "JC",
      page: 1,
      pageSize: 5000,
    });

    const list =
      response?.TransactionList ||
      response?.items ||
      response?.data?.TransactionList ||
      [];

    if (!Array.isArray(list) || list.length === 0) return null;

    const normalizeText = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const woTranId = Number(headDetails?.tran_id || 0);
    const woNo = normalizeText(
      headDetails?.workorder_no || headDetails?.wo_no || headDetails?.tran_no,
    );
    const rowSerialNo = Number(item?.serial_no || itemIndex + 1);
    const rowProductId = Number(item?.product_id || 0);
    const rowProductName = normalizeText(
      item?.product_name || item?.Product_Name || item?.name,
    );

    const scoredMatches = list
      .map((jcItem) => {
        const jcTranId = Number(
          jcItem?.tran_id || jcItem?.id || jcItem?.jc_tran_id || 0,
        );
        if (!jcTranId) return null;

        const refWoTranId = Number(
          jcItem?.ref_tran_id ||
            jcItem?.wo_tran_id ||
            jcItem?.workorder_tran_id ||
            0,
        );
        const refSerialNo = Number(
          jcItem?.ref_item_serial_no ||
            jcItem?.ref_serial_no ||
            jcItem?.ref_serial ||
            jcItem?.job_card_serial_no ||
            jcItem?.jc_serial_no ||
            jcItem?.serial_no ||
            0,
        );
        const jcProductId = Number(jcItem?.product_id || 0);
        const jcWoNo = normalizeText(jcItem?.workorder_no || jcItem?.wo_no);
        const jcNarration = normalizeText(jcItem?.narration || "");
        const jcProductName = normalizeText(
          jcItem?.product_name || jcItem?.Product_Name || "",
        );

        const hasWoTranIdMatch = woTranId > 0 && refWoTranId === woTranId;
        const hasWoNoMatch =
          Boolean(woNo) &&
          ((jcWoNo && jcWoNo === woNo) || jcNarration.includes(woNo));
        const hasSerialMatch =
          rowSerialNo > 0 && refSerialNo > 0 && refSerialNo === rowSerialNo;
        const hasProductIdMatch =
          rowProductId > 0 && jcProductId > 0 && jcProductId === rowProductId;
        const hasProductNameMatch =
          Boolean(rowProductName) &&
          Boolean(jcProductName) &&
          jcProductName === rowProductName;

        if (!hasWoTranIdMatch && !hasWoNoMatch) return null;
        if (!hasSerialMatch && !hasProductIdMatch && !hasProductNameMatch) {
          return null;
        }

        let score = 0;
        if (hasWoTranIdMatch) score += 25;
        if (woNo && jcWoNo && jcWoNo === woNo) score += 20;
        if (woNo && jcNarration.includes(woNo)) score += 12;
        if (hasSerialMatch) score += 40;
        if (hasProductIdMatch) score += 30;
        if (hasProductNameMatch) score += 15;

        return { jcTranId, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || b.jcTranId - a.jcTranId);

    return scoredMatches[0]?.jcTranId || null;
  } catch (error) {
    return null;
  }
};

const SalesForm = () => {
  const { tab, category } = useParams();
  const routerLocation = useLocation();
  const draftForm = useDraftForm();
  const restoredSnapshot = useRestoredSalesForm();
  const location = draftForm ? { ...routerLocation, state: draftForm.navigationState } : routerLocation;
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const company = location.state?.company;
  const prevLocationState = useRef(location.state);
  const isInitialLoad = useRef(true);
  const hasLoadedTransactionData = useRef(false);
  const restoredFromBomRef = useRef(false);

  const {
    transactionCode,
    transactionId,
    rowData: rowDataFromQuotation,
    dynamicHeadData,
    dynamicItemData,
    dynamicTransaction,
    documentFileName,
    backDatedEntry,
    pickupMandatory,
    isAutoTransactionNo,
    isAddMode,
    colorCode,
    customerType,
  } = location.state || {};

  const {
    selectedPickUpTransaction,
    selectedTransaction,
    selectedPickUpTransactionItems,
    clearTransactionState,
    pickUpData,
    formData,
  } = useSalesFormStore();

  const [tableData, setTableData] = useState({
    head_fields: [],
    rows: [],
    extra_forms: [],
  });
  const [transactionData, setTransactionData] = useState(() => ({
    companyData: restoredSnapshot?.formData.customerDetails || {},
    headDetails: restoredSnapshot?.formData.primaryDetails || {},
    itemDetails: restoredSnapshot?.formData.transactionItems || [],
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [isCleared, setIsCleared] = useState(false);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isPrintFormatOpen, setIsPrintFormatOpen] = useState(false);
  const [quotationPrintFormat, setQuotationPrintFormat] = useState("summary");
  const [quotationVersions, setQuotationVersions] = useState([]);
  const isFetchingFieldMaster = useRef(false);
  const isFetchingTransactionData = useRef(false);

  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails,
  );
  const setCustomerDetails = useSalesFormStore(
    (state) => state.setCustomerDetails,
  );
  const setTransactionItems = useSalesFormStore(
    (state) => state.setTransactionItems,
  );
  const clearForm = useSalesFormStore((state) => state.clearForm);

  const rights = usePermissions(transactionCode);
  const { setMode } = useMode();

  const getCachedWorkOrderStatus = (tranId) => {
    try {
      if (!tranId) return "not_started";
      const cachedStatus = sessionStorage.getItem(
        getWorkOrderStatusCacheKey(tranId),
      );
      return ["started", "finished"].includes(cachedStatus)
        ? cachedStatus
        : "not_started";
    } catch (error) {
      return "not_started";
    }
  };

  const setCachedWorkOrderStatus = (tranId, status) => {
    try {
      if (!tranId || !["started", "finished"].includes(status)) return;
      sessionStorage.setItem(getWorkOrderStatusCacheKey(tranId), status);
    } catch (error) {}
  };

  const getCachedWorkOrderRouteCardFinish = (tranId) => {
    try {
      if (!tranId) return false;
      return (
        sessionStorage.getItem(getWorkOrderRouteCardFinishCacheKey(tranId)) ===
        "true"
      );
    } catch (error) {
      return false;
    }
  };

  const setCachedWorkOrderRouteCardFinish = (tranId, isFinished) => {
    try {
      if (!tranId) return;
      if (isFinished) {
        sessionStorage.setItem(
          getWorkOrderRouteCardFinishCacheKey(tranId),
          "true",
        );
      } else {
        sessionStorage.removeItem(getWorkOrderRouteCardFinishCacheKey(tranId));
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (restoredSnapshot) return;
    if (rowDataFromQuotation) {
      if (!isAddMode) {
        setIsCopyMode(true);
      }

      setIsEditMode(false);

      const rawHeadDetails = rowDataFromQuotation.head_data || {};
      const headDetails =
        transactionCode === "WO"
          ? normalizeWorkOrderHeadStatus(rawHeadDetails)
          : rawHeadDetails;
      const itemDetails = rowDataFromQuotation.item_data || [];

      setTransactionData({
        companyData: {},
        headDetails,
        itemDetails,
      });

      if (isAutoTransactionNo) {
        const { tran_no, ...headWithoutTranNo } = headDetails;
        setPrimaryDetails(headWithoutTranNo);
      } else {
        setPrimaryDetails(headDetails);
      }
      setTransactionItems(itemDetails);

      displayToast(
        "Quotation data loaded. You can modify and save as new.",
        "info",
      );
    }
  }, [rowDataFromQuotation, restoredSnapshot]);

  useEffect(() => {
    if (restoredSnapshot) { setIsCleared(true); return; }
    const hasLocationChanged = prevLocationState.current !== location.state;
    const hasTransactionId = location.state?.transactionId;
    const draftFromBom = sessionStorage.getItem("returnFromBomDraftWorkOrder");

    if (draftFromBom && transactionCode === "WO") {
      try {
        const { headDetails, itemDetails, customerDetails } =
          JSON.parse(draftFromBom);
        setPrimaryDetails(headDetails || {});
        setTransactionItems(itemDetails || []);
        setCustomerDetails(customerDetails || {});
        setTransactionData({
          companyData: customerDetails || {},
          headDetails: headDetails || {},
          itemDetails: itemDetails || [],
        });
        setIsCleared(true);
        restoredFromBomRef.current = true;
        sessionStorage.removeItem("returnFromBomDraftWorkOrder");
      } catch (error) {
        sessionStorage.removeItem("returnFromBomDraftWorkOrder");
      }
    }

    if (
      !hasTransactionId &&
      hasLocationChanged &&
      !isEditMode &&
      !isCopyMode &&
      !draftFromBom
    ) {
      clearForm();
      clearTransactionState();
      setIsCleared(true);
    } else if (hasTransactionId || isInitialLoad.current) {
      setIsCleared(true);
    }

    prevLocationState.current = location.state;
    isInitialLoad.current = false;
  }, [location.state, isEditMode, isCopyMode, restoredSnapshot]);

  const isMasterType =
    category === "drawing-status" ||
    category === "dr" ||
    location.state?.transactionSettings?.type === "master";

  useEffect(() => {
    console.log("[DEBUG] MAIN useEffect fired", {
      transactionCode,
      transactionId,
      isMasterType,
      isEditMode,
      isCopyMode,
      headDetailsKeys: Object.keys(transactionData.headDetails).length,
    });
    if (!transactionCode) return;

    isFetchingFieldMaster.current = false;
    isFetchingTransactionData.current = false;
    hasLoadedTransactionData.current = false;

    if (!isMasterType) {
      fetchFieldMaster();
      if (
        !restoredSnapshot &&
        !isEditMode &&
        !isCopyMode &&
        !restoredFromBomRef.current &&
        Object.keys(transactionData.headDetails).length === 0
      ) {
        console.log("[DEBUG] Calling clearForm()");
        clearForm();
      }
    }
    if (transactionId && transactionCode && !isMasterType) {
      fetchFieldMasterTransactionData(transactionCode, transactionId);
    }
  }, [transactionId, transactionCode, isMasterType, restoredSnapshot]);

  useEffect(() => {
    let isActive = true;

    const loadVersions = async () => {
      if (transactionCode !== "SQ" || !transactionId) {
        setQuotationVersions([]);
        return;
      }
      try {
        const response = await fetchQuotationVersions(Number(transactionId));
        if (isActive && Array.isArray(response?.versions)) {
          setQuotationVersions(response.versions);
        }
      } catch (error) {
        if (isActive) setQuotationVersions([]);
      }
    };

    loadVersions();
    return () => {
      isActive = false;
    };
  }, [transactionCode, transactionId]);

  const handleOpenQuotationVersion = (version) => {
    if (!version?.tran_id || Number(version.tran_id) === Number(transactionId))
      return;
    navigate(`/${tab}/${category}/transactions`, {
      state: {
        transactionId: version.tran_id,
        transactionCode,
      },
    });
  };

  const isSupersededQuotation =
    transactionCode === "SQ" &&
    Boolean(transactionId) &&
    Number(transactionData.headDetails?.is_latest ?? 1) === 0;

  useEffect(() => {
    if (isInitialRestoredPickup(restoredSnapshot, { selectedPickUpTransaction, selectedPickUpTransactionItems, selectedTransaction })) return;
    if (
      isCleared &&
      selectedPickUpTransaction &&
      selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id
    ) {
      const id =
        selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id;

      fetchFieldMasterTransactionData(transactionCode, id);
    }
  }, [selectedPickUpTransaction, isCleared, restoredSnapshot]);

  const processAndApplyDynamicData = () => {
    if (!dynamicTransaction || dynamicTransaction.length === 0) {
      return;
    }

    if (!transactionCode) {
      console.warn("No transactionCode available to match configuration");
      return;
    }

    const transactionConfig = dynamicTransaction.find(
      (config) => config.tran_code === transactionCode,
    );

    if (!transactionConfig) {
      console.warn(
        `No configuration found for transactionCode: ${transactionCode}`,
      );
      return;
    }

    if (transactionConfig.head_fields && dynamicHeadData) {
      const updatedHeadDetails = { ...transactionData.headDetails };

      transactionConfig.head_fields.forEach((mapping) => {
        const sourceField = mapping.field_name;
        const targetField = mapping.copy_to_field_name;

        if (dynamicHeadData[sourceField] !== undefined) {
          updatedHeadDetails[targetField] = dynamicHeadData[sourceField];
        }
      });

      setTransactionData((prev) => ({
        ...prev,
        headDetails: updatedHeadDetails,
      }));
      if (isAutoTransactionNo) {
        const { tran_no, ...withoutTranNo } = updatedHeadDetails;
        setPrimaryDetails(withoutTranNo);
      } else {
        setPrimaryDetails(updatedHeadDetails);
      }
    }

    if (
      transactionConfig.item_fields &&
      Array.isArray(dynamicItemData) &&
      dynamicItemData.length > 0
    ) {
      let updatedItemDetails = [];

      updatedItemDetails = dynamicItemData.map((sourceItem) => {
        const newItem = {};

        transactionConfig.item_fields.forEach((mapping) => {
          const sourceField = mapping.field_name;
          const targetField = mapping.copy_to_field_name;

          if (sourceItem[sourceField] !== undefined) {
            newItem[targetField] = sourceItem[sourceField];
          }
        });

        const rate = parseFloat(newItem.rate ?? newItem.unit_rate ?? 0) || 0;
        const quantity = parseFloat(newItem.quantity ?? newItem.qty ?? 0) || 0;

        const lineAmount = rate * quantity;
        newItem.amount = lineAmount.toFixed(2);
        return newItem;
      });

      setTransactionData((prev) => ({
        ...prev,
        itemDetails: updatedItemDetails,
      }));
      setTransactionItems(updatedItemDetails);
    }

    displayToast("Dynamic data has been applied", "info");
  };

  useEffect(() => {
    if (restoredSnapshot) return;
    if (
      dynamicTransaction &&
      (dynamicHeadData || (dynamicItemData && dynamicItemData.length > 0))
    ) {
      processAndApplyDynamicData();
    }
  }, [dynamicTransaction, dynamicHeadData, dynamicItemData, restoredSnapshot]);

  const handleCloseEditMode = () => {
    setIsCopyMode(false);
    setIsEditMode(false);
  };

  const handleCloseAddMode = () => {
    navigate(-1);
    clearTransactionState();
  };

  useEffect(() => {
    return () => {
      if (restoredSnapshot) return;
      clearForm();
      clearTransactionState();
    };
  }, [clearForm, clearTransactionState, restoredSnapshot]);

  useEffect(() => {
    if (isInitialRestoredPickup(restoredSnapshot, { selectedPickUpTransaction, selectedPickUpTransactionItems, selectedTransaction })) return;
    if (selectedPickUpTransactionItems?.length > 0) {
      const mappedHead = {};

      const pickUpConfigs = Array.isArray(pickUpData)
        ? pickUpData
        : pickUpData?.head_fields || pickUpData?.item_fields
          ? [pickUpData]
          : Object.values(pickUpData || {});
      const selectedPickUpConfig =
        pickUpConfigs.find(
          (config) => config?.tran_code === selectedTransaction?.tran_code,
        ) || pickUpConfigs[0];
      const headFields = selectedPickUpConfig?.head_fields || [];
      const itemFields = selectedPickUpConfig?.item_fields || [];

      headFields.forEach(
        ({ field_name, copy_to_field_name, copy_to_table_type }) => {
          const transactionData =
            selectedPickUpTransaction?.[selectedTransaction?.tran_code];
          const value = transactionData?.[field_name];

          if (value !== undefined && copy_to_table_type === "head") {
            mappedHead[copy_to_field_name] = value;
          }
        },
      );

      selectedPickUpTransactionItems.forEach((item) => {
        itemFields.forEach(
          ({ field_name, copy_to_field_name, copy_to_table_type }) => {
            if (copy_to_table_type === "head") {
              mappedHead[copy_to_field_name] = item[field_name] ?? "";
            }
          },
        );
      });

      if (!isEditMode) {
        if (isAutoTransactionNo) {
          delete mappedHead.tran_no;
        }
        setPrimaryDetails(mappedHead);
      }
    }
  }, [
    selectedPickUpTransactionItems,
    selectedPickUpTransaction,
    pickUpData,
    restoredSnapshot,
    selectedTransaction,
    isEditMode,
    isAutoTransactionNo,
  ]);

  const fetchFieldMasterTransactionData = async (
    transactionCode,
    transactionId,
  ) => {
    console.log("[DEBUG] fetchFieldMasterTransactionData CALLED", {
      transactionCode,
      transactionId,
      isEditMode,
      locationTransactionId: location.state?.transactionId,
    });
    if (isFetchingTransactionData.current) {
      console.log(
        "[DEBUG] fetchFieldMasterTransactionData BLOCKED by ref guard",
      );
      return;
    }
    isFetchingTransactionData.current = true;
    try {
      const response = await fetchTransactionByCodeAndId(
        transactionCode,
        transactionId,
      );
      console.log("[DEBUG] fetchFieldMasterTransactionData RESPONSE", {
        hasDescription: !!response.description,
        headKeys: Object.keys(response.head_data || {}),
        itemCount: (response.item_data || []).length,
      });

      if (response.description) {
        console.log(
          "[DEBUG] fetchFieldMasterTransactionData ERROR RESPONSE",
          response.description,
        );
        displayToast(response.description, "error");
      } else {
        const companyData = response.company_data || {};
        const rawHeadDetails = response.head_data || {};
        let headDetails =
          transactionCode === "WO"
            ? normalizeWorkOrderHeadStatus(rawHeadDetails)
            : rawHeadDetails;
        const itemDetails = response.item_data || [];

        const bomNumberIsEmpty = [undefined, null, "", "-", "N/A"].includes(
          headDetails?.bom_number,
        );
        const linkedBomId = Number(headDetails?.bom || 0);

        if (transactionCode === "WO" && bomNumberIsEmpty && linkedBomId) {
          const wbomResponse = await fetchTransactionByCodeAndId(
            "WBOM",
            linkedBomId,
          );
          const wbomTranNo = wbomResponse?.head_data?.tran_no;

          if (wbomTranNo) {
            headDetails = {
              ...headDetails,
              bom_number: wbomTranNo,
            };
          }
        }

        if (location.state?.transactionId) {
          console.log(
            "[DEBUG] Setting transactionData, headDetails keys:",
            Object.keys(headDetails).length,
            "itemDetails count:",
            itemDetails.length,
          );
          setTransactionData({
            companyData,
            headDetails,
            itemDetails,
          });

          if (!isEditMode) {
            console.log(
              "[DEBUG] Calling setPrimaryDetails + setTransactionItems",
            );
            setPrimaryDetails(headDetails);
            setTransactionItems(itemDetails);
            hasLoadedTransactionData.current = true;
          } else {
            console.log(
              "[DEBUG] SKIPPED setPrimaryDetails/setTransactionItems because isEditMode =",
              isEditMode,
            );
          }
        } else {
          console.log(
            "[DEBUG] SKIPPED setting data because location.state.transactionId is falsy",
          );
        }
      }
    } catch (error) {
      console.error(
        "[DEBUG] fetchFieldMasterTransactionData CATCH error:",
        error,
      );
      displayToast("Failed to fetch field master transaction data", "error");
    } finally {
      isFetchingTransactionData.current = false;
    }
  };

  const fetchFieldMaster = async () => {
    console.log(
      "[DEBUG] fetchFieldMaster CALLED, isFetchingFieldMaster:",
      isFetchingFieldMaster.current,
    );
    if (isFetchingFieldMaster.current) {
      console.log("[DEBUG] fetchFieldMaster BLOCKED by ref guard");
      return;
    }
    isFetchingFieldMaster.current = true;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      console.log("[DEBUG] fetchFieldMaster RESPONSE", {
        hasDescription: !!response.description,
        headFieldCount: (response.items?.head_fields || []).length,
        itemFieldCount: (response.items?.item_fields || []).length,
      });

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        const headFields = response.items?.head_fields || [];
        const rows = response.items?.item_fields || [];
        const extraForms = response.items?.extra_forms || [];

        const updatedHeadFields = [...headFields];
        console.log("[DEBUG] fetchFieldMaster setTableData", {
          headFieldCount: headFields.length,
          rowFieldCount: rows.length,
          extraFormsCount: extraForms.length,
        });
        setTableData({
          head_fields: updatedHeadFields,
          rows,
          extra_forms: extraForms,
        });
      }
    } catch (error) {
      console.error("[DEBUG] fetchFieldMaster CATCH error:", error);
      displayToast("Failed to fetch field master data", "error");
    } finally {
      isFetchingFieldMaster.current = false;
      setIsLoading(false);
    }
  };

  const handlePrintClick = (printFormat) => {
    if (!rights.can_print) {
      displayToast(`You don't have permission to print ${category}`, "error");
      return;
    }

    if (isSupersededQuotation) {
      displayToast(
        "This is a superseded revision \u2014 only the latest revision can be printed",
        "error",
      );
      return;
    }

    if (
      !transactionData.headDetails ||
      Object.keys(transactionData.headDetails).length === 0
    ) {
      displayToast("No transaction data available to print", "error");
      return;
    }

    if (transactionCode === "SQ" && typeof printFormat !== "string") {
      setIsPrintFormatOpen(true);
      return;
    }

    setIsPrintFormatOpen(false);
    const fileName = documentFileName || "invoice";

    navigate(`/${tab}/${category}/transactions/${fileName}`, {
      state: {
        formData: {
          headDetails: transactionData.headDetails,
          itemDetails: transactionData.itemDetails,
          companyData: transactionData.companyData,
          rowData: tableData?.rows || [],
          transactionCode: transactionCode,
          ...(transactionCode === "SQ" ? { printFormat } : {}),
        },
      },
    });
  };

  const handleCopyClick = () => {
    setIsCopyMode(true);
    setIsEditMode(false);
    setPrimaryDetails(transactionData.headDetails);
    setTransactionItems(transactionData.itemDetails);
    displayToast(
      "Form is now in copy mode. You can modify and save as a new transaction.",
      "info",
    );
  };

  const handleEditClick = () => {
    if (!rights.can_edit) {
      displayToast(`You don't have permission to edit ${category}`, "error");
      return;
    }

    if (isSupersededQuotation) {
      displayToast(
        "This is a superseded revision \u2014 only the latest revision can be edited",
        "error",
      );
      return;
    }

    setIsEditMode(true);
    setIsCopyMode(false);
    displayToast(
      "Form is now in edit mode. You can modify and update the transaction.",
      "info",
    );
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(`You don't have permission to delete ${category}`, "error");
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(
        transactionCode,
        transactionId,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigate(`/${tab}/${category}`, {
          state: { transactionCode, forceRefresh: true },
        });
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleWorkOrderCardAction = async (fieldName) => {
    const tranId = transactionData?.headDetails?.tran_id;
    if (!tranId) {
      displayToast(
        "Please save the transaction before updating status",
        "error",
      );
      return;
    }

    try {
      const itemDetails = mergeItemDetails(
        transactionData?.itemDetails || [],
        formData?.transactionItems || [],
      );
      const workOrderCardLabel = getWorkOrderCardLabel(
        formData?.primaryDetails?.type || transactionData?.headDetails?.type,
      );

      const hasRouteCardField = (tableData?.rows || []).some(
        (field) =>
          ["ROUTE_CARD", "CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
            String(field.field_type || "").toUpperCase(),
          ) ||
          ["ROUTE_CARD", "CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
            String(field.data_type || "").toUpperCase(),
          ) ||
          ["CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
            String(field.field_name || "").toUpperCase(),
          ),
      );

      const hasAnyCreatedRouteCard = itemDetails.some((item) =>
        hasCreatedRouteCard(item),
      );

      if (
        fieldName === "start" &&
        hasRouteCardField &&
        !hasAnyCreatedRouteCard
      ) {
        displayToast(
          `Please create ${workOrderCardLabel} before starting work order`,
          "error",
        );
        return;
      }

      if (
        fieldName === "start" &&
        hasRouteCardField &&
        hasAnyCreatedRouteCard
      ) {
        const expectedItems = itemDetails.filter((item) =>
          hasCreatedRouteCard(item),
        );

        try {
          for (let index = 0; index < expectedItems.length; index += 1) {
            const item = expectedItems[index];
            const lookupId = getRouteCardLookupId(item);

            if (lookupId) {
              const routeCard = await fetchRouteCard(
                `${lookupId}?t=${Date.now()}`,
              );
              const items = Array.isArray(routeCard?.item_data)
                ? routeCard.item_data
                : Array.isArray(routeCard?.data?.item_data)
                  ? routeCard.data.item_data
                  : [];

              const hasValidOperation = items.some(
                (routeItem) =>
                  (routeItem?.operation || routeItem?.operations) &&
                  (routeItem?.work_station || routeItem?.workStation),
              );

              if (!hasValidOperation) {
                displayToast(
                  `Please create ${workOrderCardLabel} before starting work order`,
                  "error",
                );
                return;
              }
            }
          }
        } catch (err) {
          displayToast(
            "Error validating route card. Please try again.",
            "error",
          );
          return;
        }
      }

      if (fieldName === "finish" && hasRouteCardField) {
        const expectedItems = itemDetails.filter((item) =>
          Boolean(
            item?.product_id ||
            item?.product_name ||
            item?.quantity ||
            item?.serial_no ||
            item?.tran_id,
          ),
        );

        if (expectedItems.length === 0) {
          displayToast(
            "Cannot finish before finishing all route cards",
            "error",
          );
          return;
        }

        const resolvedLookupIdsByItem = [];

        for (let index = 0; index < expectedItems.length; index += 1) {
          const item = expectedItems[index];
          const directLookupId = getRouteCardLookupId(item);
          const resolvedLookupId =
            directLookupId ||
            (await resolveWorkOrderItemJobCardId({
              item,
              itemIndex: index,
              headDetails: transactionData?.headDetails || {},
            }));

          if (!resolvedLookupId) {
            displayToast(
              "Cannot finish before finishing all route cards",
              "error",
            );
            return;
          }

          resolvedLookupIdsByItem[index] = resolvedLookupId;
        }

        setIsLoading(true);
        try {
          const routeCardFinishedByLookupId = new Map();
          const routeCardLookupIds = Array.from(
            new Set(resolvedLookupIdsByItem.filter(Boolean)),
          );

          if (routeCardLookupIds.length > 0) {
            const routeCardResponses = await Promise.all(
              routeCardLookupIds.map(async (lookupId) => {
                try {
                  return await fetchRouteCard(lookupId);
                } catch (err) {
                  return null;
                }
              }),
            );

            routeCardLookupIds.forEach((lookupId, responseIndex) => {
              routeCardFinishedByLookupId.set(
                lookupId,
                isRouteCardFinished(routeCardResponses[responseIndex]),
              );
            });
          }

          const areAllItemsFinished = expectedItems.every((item, index) => {
            const lookupId =
              resolvedLookupIdsByItem[index] || getRouteCardLookupId(item);
            const locallyFinished = isRouteCardLocallyFinished(item);

            if (lookupId && routeCardFinishedByLookupId.has(lookupId)) {
              return (
                Boolean(routeCardFinishedByLookupId.get(lookupId)) ||
                locallyFinished
              );
            }

            return locallyFinished;
          });

          if (!areAllItemsFinished) {
            displayToast(
              "Cannot finish before finishing all route cards",
              "error",
            );
            return;
          }
        } catch (error) {
          displayToast(
            "Cannot finish before finishing all route cards",
            "error",
          );
          return;
        } finally {
          setIsLoading(false);
        }
      }

      const result = await updateJobCardStatus(fieldName, tranId);

      if (result?.description || result?.message) {
        displayToast(
          result?.description ||
            result?.message ||
            "An unexpected error occurred while updating status",
          "error",
        );
        return;
      }

      const updatedStatus = fieldName === "finish" ? "finished" : "started";
      const statusMessage =
        fieldName === "finish"
          ? "Work order finished successfully"
          : "Work order started successfully";

      if (result?.status === "success") {
        displayToast(statusMessage, "success");
        setCachedWorkOrderStatus(tranId, updatedStatus);
        setTransactionData((prev) => ({
          ...prev,
          headDetails: {
            ...prev.headDetails,
            status: updatedStatus,
            start: fieldName === "start" ? "started" : prev.headDetails?.start,
            finish:
              fieldName === "finish" ? "finished" : prev.headDetails?.finish,
            route_card_start:
              fieldName === "start"
                ? "started"
                : prev.headDetails?.route_card_start,
            route_card_finish:
              fieldName === "finish"
                ? "finished"
                : prev.headDetails?.route_card_finish,
          },
        }));
        setPrimaryDetails({
          ...transactionData?.headDetails,
          status: updatedStatus,
          start:
            fieldName === "start"
              ? "started"
              : transactionData?.headDetails?.start,
          finish:
            fieldName === "finish"
              ? "finished"
              : transactionData?.headDetails?.finish,
          route_card_start:
            fieldName === "start"
              ? "started"
              : transactionData?.headDetails?.route_card_start,
          route_card_finish:
            fieldName === "finish"
              ? "finished"
              : transactionData?.headDetails?.route_card_finish,
        });
      }
    } catch (error) {
      displayToast("An error occurred while updating status", "error");
    }
  };

  const isViewMode = Boolean(transactionId) && !isCopyMode && !isEditMode;

  useAutoRefresh(
    () => fetchFieldMasterTransactionData(transactionCode, transactionId),
    { enabled: isViewMode && Boolean(transactionId && transactionCode) },
  );

  const isWorkOrderTransaction = transactionCode === "WO";

  const workOrderItemDetails = mergeItemDetails(
    transactionData?.itemDetails || [],
    formData?.transactionItems || [],
  );

  const hasRouteCardField = (tableData?.rows || []).some(
    (field) =>
      ["ROUTE_CARD", "CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
        String(field.field_type || "").toUpperCase(),
      ) ||
      ["ROUTE_CARD", "CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
        String(field.data_type || "").toUpperCase(),
      ) ||
      ["CREATE_JOBCARD", "CREATE_JOB_CARD"].includes(
        String(field.field_name || "").toUpperCase(),
      ),
  );

  const hasAnyCreatedRouteCard = workOrderItemDetails.some((item) =>
    hasCreatedRouteCard(item),
  );

  const expectedWorkOrderItems = workOrderItemDetails.filter((item) =>
    Boolean(
      item?.product_id ||
      item?.product_name ||
      item?.quantity ||
      item?.serial_no ||
      item?.tran_id,
    ),
  );

  const allExpectedItemsHaveFinishedRouteCard =
    expectedWorkOrderItems.length > 0 &&
    expectedWorkOrderItems.every(
      (item) =>
        Boolean(item?.route_card_head_finished) ||
        isFinishedToken(item?.route_card_finish) ||
        isFinishedToken(item?.job_card_finish),
    );

  const cachedWorkOrderRouteCardFinish = getCachedWorkOrderRouteCardFinish(
    transactionData?.headDetails?.tran_id,
  );

  const canFinishWorkOrderFromRouteCardState =
    allExpectedItemsHaveFinishedRouteCard || cachedWorkOrderRouteCardFinish;

  const headWorkOrderStatus = getWorkOrderStatus(transactionData?.headDetails);
  const cachedWorkOrderStatus = getCachedWorkOrderStatus(
    transactionData?.headDetails?.tran_id,
  );
  const workOrderStatus =
    headWorkOrderStatus !== "not_started"
      ? headWorkOrderStatus
      : cachedWorkOrderStatus;

  useEffect(() => {
    const tranId = transactionData?.headDetails?.tran_id;
    if (!tranId) return;
    if (workOrderStatus === "started" || workOrderStatus === "finished") {
      setCachedWorkOrderStatus(tranId, workOrderStatus);
    }
  }, [transactionData?.headDetails?.tran_id, workOrderStatus]);

  useEffect(() => {
    const tranId = transactionData?.headDetails?.tran_id;
    if (!tranId) return;

    if (workOrderStatus === "finished") {
      setCachedWorkOrderRouteCardFinish(tranId, false);
      return;
    }

    if (allExpectedItemsHaveFinishedRouteCard) {
      setCachedWorkOrderRouteCardFinish(tranId, true);
    }
  }, [
    transactionData?.headDetails?.tran_id,
    workOrderStatus,
    allExpectedItemsHaveFinishedRouteCard,
  ]);

  const disableWorkOrderStart =
    !transactionData?.headDetails?.tran_id ||
    workOrderStatus === "started" ||
    workOrderStatus === "finished";
  const disableWorkOrderFinish =
    !transactionData?.headDetails?.tran_id ||
    workOrderStatus !== "started" ||
    workOrderStatus === "finished" ||
    (hasRouteCardField && !canFinishWorkOrderFromRouteCardState);

  const handleBackClick = () => {
    navigate(-1);
  };

  const modeHighlight = isEditMode
    ? "edit-mode"
    : !isViewMode && !isCopyMode && !isEditMode
      ? "add-mode"
      : "";

  useEffect(() => {
    if (isEditMode) {
      setMode("edit");
    } else if (isCopyMode) {
      setMode("copy");
    } else if (!transactionId) {
      setMode("add");
    } else {
      setMode("view");
    }
  }, [isEditMode, isCopyMode, transactionId, setMode]);

  const categorySubtitleMap = {
    enquiry: "Create and manage customer enquiries",
    quotation: "Create and manage sales quotations",
    quote: "Create and manage sales quotations",
    "customer-po": "Create and manage customer orders",
    "work-order": "Create and manage work orders",
    invoice: "Create and manage invoices",
  };
  const headerSubtitle =
    categorySubtitleMap[category] ||
    `Create and manage ${capitalizeFirstLetterOfEachWord(
      (category || "").replace(/-/g, " "),
    )}`;

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="sales-form-container">
          {category !== "drawing-status" &&
            category !== "dr" &&
            location.state?.transactionSettings?.type !== "master" && (
              <header className="sales-form-header">
                <div className="sales-form-header-heading">
                  {!isEditMode && transactionId && (
                    <button
                      className="back-button-transaction"
                      onClick={handleBackClick}
                    >
                      <ArrowLeft size={22} />
                    </button>
                  )}
                  <span className="sales-form-header-icon-badge">
                    <img
                      src={category === "enquiry" ? Enquiry : Quotation}
                      className="sales-form-header-icon"
                      alt="Form Type"
                    />
                  </span>
                  <div className="sales-form-header-title-group">
                    <h5>
                      {capitalizeFirstLetterOfEachWord(category)}
                      {isCopyMode && " - Copy Mode"}
                      {isEditMode && " - Edit Mode"}
                      {isAddMode && " - Add Mode"}
                      {transactionCode === "SQ" &&
                        transactionId &&
                        transactionData.headDetails?.revision_no !==
                          undefined &&
                        transactionData.headDetails?.revision_no !== null && (
                          <span
                            className={`quotation-revision-badge${
                              Number(
                                transactionData.headDetails?.is_latest ?? 1,
                              ) === 0
                                ? " quotation-revision-badge--superseded"
                                : ""
                            }`}
                          >
                            R
                            {Number(transactionData.headDetails?.revision_no) ||
                              0}
                            {Number(
                              transactionData.headDetails?.is_latest ?? 1,
                            ) === 0
                              ? " \u00b7 Superseded"
                              : ""}
                          </span>
                        )}
                    </h5>
                    <span className="sales-form-header-subtitle">
                      {headerSubtitle}
                    </span>
                  </div>
                </div>
                <div className="sales-form-header-heading">
                  {isWorkOrderTransaction && (
                    <div className="sales-form-status-button-container">
                      <button
                        className="sales-form-status-button"
                        onClick={() => handleWorkOrderCardAction("start")}
                        disabled={disableWorkOrderStart}
                      >
                        {workOrderStatus === "started" ||
                        workOrderStatus === "finished"
                          ? "Started"
                          : "Start"}
                      </button>
                      <button
                        className="sales-form-status-button"
                        onClick={() => handleWorkOrderCardAction("finish")}
                        disabled={disableWorkOrderFinish}
                      >
                        {workOrderStatus === "finished" ? "Finished" : "Finish"}
                      </button>
                    </div>
                  )}
                  {isCopyMode ||
                  isEditMode ||
                  (!transactionId && !isCopyMode && !isEditMode) ? (
                    <X
                      className="sales-form-icons cursor-pointer"
                      onClick={
                        !transactionId && !isCopyMode && !isEditMode
                          ? handleCloseAddMode
                          : handleCloseEditMode
                      }
                      size={24}
                      title="Close Mode"
                    />
                  ) : (
                    transactionCode &&
                    transactionId && (
                      <>
                        <img
                          src={Copy}
                          className="sales-form-icons"
                          alt="Copy"
                          onClick={handleCopyClick}
                          style={{
                            cursor: rights.can_add ? "pointer" : "not-allowed",
                            opacity: rights.can_add ? 1 : 0.5,
                          }}
                          title={
                            rights.can_add
                              ? "Copy Transaction"
                              : "No permission to copy"
                          }
                        />
                        <img
                          src={Edit}
                          className="sales-form-icons"
                          alt="Edit"
                          onClick={handleEditClick}
                          style={{
                            cursor:
                              rights.can_edit && !isSupersededQuotation
                                ? "pointer"
                                : "not-allowed",
                            opacity:
                              rights.can_edit && !isSupersededQuotation
                                ? 1
                                : 0.5,
                          }}
                          title={
                            isSupersededQuotation
                              ? "Superseded revision \u2014 open the latest revision to edit"
                              : rights.can_edit
                                ? "Edit Transaction"
                                : "No permission to edit"
                          }
                        />
                        <img
                          src={Delete}
                          className="sales-form-icons"
                          alt="Delete"
                          onClick={handleDeleteClick}
                          style={{
                            cursor: rights.can_delete
                              ? "pointer"
                              : "not-allowed",
                            opacity: rights.can_delete ? 1 : 0.5,
                          }}
                          title={
                            rights.can_delete
                              ? "Delete Transaction"
                              : "No permission to delete"
                          }
                        />
                        <img
                          src={Print}
                          className="sales-form-icons"
                          onClick={handlePrintClick}
                          style={{
                            cursor:
                              rights.can_print && !isSupersededQuotation
                                ? "pointer"
                                : "not-allowed",
                            opacity:
                              rights.can_print && !isSupersededQuotation
                                ? 1
                                : 0.5,
                          }}
                          title={
                            isSupersededQuotation
                              ? "Superseded revision \u2014 open the latest revision to print"
                              : rights.can_print
                                ? "Print Transaction"
                                : "No permission to print"
                          }
                          alt="Print"
                        />
                      </>
                    )
                  )}
                </div>
              </header>
            )}
          <div className="sales-form-content">
            {category !== "drawing-status" &&
              category !== "dr" &&
              transactionCode !== "WO" &&
              location.state?.transactionSettings?.type !== "master" && (
                <BuyerDetails
                  tab={tab}
                  company={company ? company : transactionData.companyData}
                  isEditMode={isEditMode}
                  onCompanyDataChange={(updatedCompany) => {
                    setTransactionData((prev) => ({
                      ...prev,
                      companyData: updatedCompany,
                    }));
                  }}
                  allowNavigation={!isEditMode && !draftForm}
                  customerType={customerType}
                />
              )}

            {isMasterType ? (
              <DrawingStatusMaster
                transactionCode={transactionCode}
                heading=""
                selectedDetail=""
                modeOfEdit={isAddMode ? "add" : ""}
                onClose={() => navigate(-1)}
                extraForms={tableData.extra_forms}
                newEntryTranCode={transactionCode}
                customerType={customerType}
                isPopupMode={false}
              />
            ) : (
              <PrimaryDocument
                transactionCode={transactionCode}
                headFields={tableData.head_fields}
                extraForms={tableData.extra_forms}
                isViewMode={isViewMode}
                isCopyMode={isCopyMode}
                backDatedEntry={backDatedEntry}
                pickupMandatory={pickupMandatory}
                isAutoTransactionNo={isAutoTransactionNo}
                modeHighlight={modeHighlight}
                isInEditMode={isEditMode}
              />
            )}
          </div>
          {category !== "drawing-status" &&
            category !== "dr" &&
            location.state?.transactionSettings?.type !== "master" && (
              <TransactionTable
                rowData={tableData?.rows || []}
                transactionCode={transactionCode || ""}
                transactionData={transactionData}
                workOrderStatus={workOrderStatus}
                isViewMode={isViewMode}
                initialData={
                  isEditMode ? transactionData.itemDetails : undefined
                }
                isEditMode={isEditMode}
                isCopyMode={isCopyMode}
                setIsEditMode={setIsEditMode}
                headFields={tableData.head_fields}
                locationState={location.state}
                pickupMandatory={pickupMandatory}
                isAutoTransactionNo={isAutoTransactionNo}
                companyData={transactionData.companyData}
                onTransactionUpdate={(updatedData) => {
                  if (updatedData.companyData) {
                    setTransactionData((prev) => ({
                      ...prev,
                      companyData: updatedData.companyData,
                    }));
                  }
                  if (updatedData.headDetails) {
                    setTransactionData((prev) => ({
                      ...prev,
                      headDetails: updatedData.headDetails,
                    }));
                  }
                  if (updatedData.itemDetails) {
                    setTransactionData((prev) => ({
                      ...prev,
                      itemDetails: updatedData.itemDetails,
                    }));
                  }
                  setIsEditMode(false);
                }}
                shouldNavigateAfterSubmit={true}
                modeHighlight={modeHighlight}
              />
            )}
          {transactionCode === "SQ" &&
            transactionId &&
            quotationVersions.length > 1 && (
              <div className="quotation-versions-panel">
                <div className="quotation-versions-title">
                  Quotation Versions
                </div>
                <table className="quotation-versions-table">
                  <thead>
                    <tr>
                      <th>VERSION</th>
                      <th>QUOTE NO</th>
                      <th>DATE</th>
                      <th>GRAND TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationVersions.map((version) => {
                      const revisionNo = Number(version.revision_no) || 0;
                      const isCurrentOpen =
                        Number(version.tran_id) === Number(transactionId);
                      const isLatestVersion =
                        Number(version.is_latest ?? 1) !== 0;
                      const displayQuoteNo = `${version.tran_no || ""}${
                        revisionNo > 0 ? `-R${revisionNo}` : ""
                      }`;
                      const grandTotal =
                        version.gross_amt ?? version.net_amt ?? null;
                      return (
                        <tr
                          key={version.tran_id}
                          className={
                            isCurrentOpen
                              ? "quotation-versions-row--current"
                              : ""
                          }
                        >
                          <td>
                            <span
                              className={`revision-chip${
                                isLatestVersion ? " revision-chip--current" : ""
                              }`}
                            >
                              R{revisionNo}
                            </span>
                            {isLatestVersion ? " \u00b7 latest" : ""}
                          </td>
                          <td>{displayQuoteNo}</td>
                          <td>
                            {version.date
                              ? String(version.date).split("T")[0]
                              : "\u2014"}
                          </td>
                          <td>
                            {grandTotal !== null && grandTotal !== ""
                              ? `Rs ${grandTotal}`
                              : "\u2014"}
                          </td>
                          <td>
                            {isCurrentOpen ? (
                              <span className="quotation-versions-current-label">
                                Viewing
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="quotation-versions-view-link"
                                onClick={() =>
                                  handleOpenQuotationVersion(version)
                                }
                              >
                                View &rarr;
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {isPrintFormatOpen && (
        <div className="download-format-modal-overlay">
          <div
            className="download-format-modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sq-print-format-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsPrintFormatOpen(false);
            }}
          >
            <div className="download-format-modal-header">
              <h2 id="sq-print-format-title">Select Print Format</h2>
              <button
                type="button"
                className="download-format-modal-close"
                aria-label="Close print format"
                onClick={() => setIsPrintFormatOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="download-format-modal-content">
              {["summary", "detail"].map((format) => (
                <label
                  key={format}
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <input
                    type="radio"
                    name="sq-print-format"
                    value={format}
                    checked={quotationPrintFormat === format}
                    onChange={() => setQuotationPrintFormat(format)}
                    autoFocus={format === quotationPrintFormat}
                  />
                  {format === "summary" ? "Summary Format" : "Detail Format"}
                </label>
              ))}
            </div>
            <div className="download-format-modal-actions">
              <button
                type="button"
                className="download-format-modal-cancel"
                onClick={() => setIsPrintFormatOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="download-format-modal-confirm"
                onClick={() => handlePrintClick(quotationPrintFormat)}
              >
                Show Preview
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default SalesForm;
